import type { SchematicVersion } from "@models/project";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDownRounded";
import CheckIcon from "@mui/icons-material/CheckRounded";
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useVersions } from "../hooks/useVersions";

interface VersionSelectMenuProps {
  projectId: string;
  activeVersionId: string | null;
  onSelect: (version: SchematicVersion) => void;
  disabled?: boolean;
}

/** Dropdown used inside the chat header to switch the pinned version. */
export function VersionSelectMenu({
  projectId,
  activeVersionId,
  onSelect,
  disabled,
}: VersionSelectMenuProps) {
  const { data: versions } = useVersions(projectId);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!versions || versions.length === 0) return null;

  const current =
    versions.find((v) => v.id === activeVersionId) ??
    versions.find((v) => v.is_current) ??
    versions[0];

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          textTransform: "none",
          borderColor: "border.default",
          color: "text.secondary",
          py: 0.25,
          minHeight: 28,
          fontFamily: (t) => t.fonts.mono,
          fontSize: "0.72rem",
          letterSpacing: "0.02em",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        v{current.version_number}
        {current.is_current ? "" : " (pinned)"}
      </Button>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{
          paper: { sx: { maxHeight: 320, minWidth: 220 } },
        }}
      >
        {versions.map((v) => {
          const selected = v.id === current.id;
          return (
            <MenuItem
              key={v.id}
              selected={selected}
              onClick={() => {
                onSelect(v);
                setAnchor(null);
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {selected ? <CheckIcon fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    v{v.version_number}
                    {v.is_current ? " · current" : ""}
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {v.component_count} comp · {v.net_count} nets ·{" "}
                    {v.uploaded_at
                      ? new Date(v.uploaded_at).toLocaleDateString()
                      : "unknown"}
                  </Typography>
                }
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
